<template>
  <div class="af-root">

    <!-- ══════════════════ MODE RECHERCHE ══════════════════ -->
    <template v-if="!manualMode">

      <div class="af-field">
        <label class="af-label">
          <span class="af-label-dot"></span>
          Adresse
        </label>

        <div ref="inputWrapper" class="af-input-wrap" :class="{ 'is-focused': isFocused, 'is-filled': selected }">
          <div class="af-input-icon">
            <i class="fas fa-map-marker-alt"></i>
          </div>
          <input
            ref="input"
            v-model="query"
            @input="onInput"
            @focus="isFocused = true"
            @blur="onBlur"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape="closeSuggestions"
            type="text"
            autocomplete="off"
            spellcheck="false"
            placeholder="12 rue des Fleurs, Lyon…"
            class="af-input"
          />
          <div class="af-input-actions">
            <span v-if="loading" class="af-spinner"></span>
            <button v-if="selected && !loading" @mousedown.prevent="clear" class="af-clear-btn" title="Effacer">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <p v-if="error" class="af-error">
          <i class="fas fa-exclamation-circle"></i> {{ error }}
        </p>
      </div>

      <!-- Dropdown -->
      <transition name="af-drop">
        <ul
          v-if="suggestions.length > 0"
          class="af-dropdown"
          :style="dropdownStyle"
          role="listbox"
          @mousedown.prevent
        >
          <li
            v-for="(item, index) in suggestions"
            :key="index"
            @mousedown.prevent="selectAddress(item)"
            @mouseover="highlighted = index"
            :class="['af-option', { 'is-active': highlighted === index }]"
            role="option"
          >
            <div class="af-option-pin">
              <i class="fas fa-map-pin"></i>
            </div>
            <div class="af-option-text">
              <span class="af-option-main">{{ item._label.split(',')[0] }}</span>
              <span class="af-option-sub">{{ item._label.split(',').slice(1).join(',').trim() }}</span>
            </div>
          </li>
        </ul>
      </transition>

      <!-- Récap adresse sélectionnée -->
      <transition name="af-reveal">
        <div v-if="selected" class="af-recap">
          <div class="af-recap-row af-recap-full">
            <span class="af-recap-label">Rue</span>
            <span class="af-recap-value">{{ dataModel.adresse1 }}</span>
          </div>
          <div class="af-recap-divider"></div>
          <div class="af-recap-row">
            <span class="af-recap-label">Code postal</span>
            <span class="af-recap-value">{{ dataModel.code_postal }}</span>
          </div>
          <div class="af-recap-row">
            <span class="af-recap-label">Ville</span>
            <span class="af-recap-value">{{ dataModel.ville }}</span>
          </div>
        </div>
      </transition>

      <div class="af-manual-link-wrap">
        <button type="button" @click="switchToManual" class="af-link">
          <i class="fas fa-pen"></i>
          Adresse introuvable ? Saisir manuellement
        </button>
      </div>

    </template>

    <!-- ══════════════════ MODE MANUEL ══════════════════ -->
    <template v-else>
      <div class="af-manual-header">
        <div class="af-label">
          <span class="af-label-dot"></span>
          Saisie manuelle
        </div>
        <button type="button" @click="switchToSearch" class="af-link">
          <i class="fas fa-search"></i>
          Retour à la recherche
        </button>
      </div>

      <div class="af-manual-grid">
        <div class="af-field af-field--full">
          <label class="af-label-sm">Adresse</label>
          <div class="af-input-wrap">
            <input v-model="dataModel.adresse1" @input="emitUpdate" type="text"
              placeholder="12 rue des Fleurs" class="af-input af-input--plain" />
          </div>
        </div>

        <div class="af-field af-field--full">
          <label class="af-label-sm">
            Complément <span class="af-optional">optionnel</span>
          </label>
          <div class="af-input-wrap">
            <input v-model="dataModel.adresse2" @input="emitUpdate" type="text"
              placeholder="Bâtiment B, lieu-dit…" class="af-input af-input--plain" />
          </div>
        </div>

        <div class="af-field">
          <label class="af-label-sm">Code postal</label>
          <div class="af-input-wrap">
            <input v-model="dataModel.code_postal" @input="emitUpdate" type="text"
              placeholder="69000" maxlength="5" class="af-input af-input--plain" />
          </div>
        </div>

        <div class="af-field">
          <label class="af-label-sm">Ville</label>
          <div class="af-input-wrap">
            <input v-model="dataModel.ville" @input="emitUpdate" type="text"
              placeholder="Lyon" class="af-input af-input--plain" />
          </div>
        </div>
      </div>

      <div class="af-notice">
        <i class="fas fa-info-circle"></i>
        Coordonnées GPS non disponibles en saisie manuelle.
      </div>

    </template>

    <!-- ══════════════════ TOGGLES RÉGIE / LOCATAIRE ══════════════════ -->
    <transition name="af-reveal">
      <div v-if="contextLoc && (selected || manualMode)" class="af-extra">

        <div class="af-extra-label">
          <span class="af-label-dot af-label-dot--sm"></span>
          Contexte locatif
        </div>

        <!-- Toggle Régie -->
        <div class="af-toggle" :class="{ 'is-active': hasRegie }" @click="toggleRegie">
          <div class="af-toggle-check" :class="{ 'is-checked': hasRegie }">
            <i v-if="hasRegie" class="fas fa-check"></i>
          </div>
          <div class="af-toggle-icon af-toggle-icon--regie">
            <i class="fas fa-building"></i>
          </div>
          <div class="af-toggle-content">
            <div class="af-toggle-title">Gérée par une régie</div>
            <div class="af-toggle-sub">Agence ou gestionnaire immobilier</div>
          </div>
          <div class="af-toggle-chevron" :class="{ 'is-open': hasRegie }">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <transition name="af-slide">
          <div v-if="hasRegie" class="af-toggle-block">
            <client-block
              title="Régie"
              icon="fas fa-building"
              id-prefix="regie"
              :value="dataModel.regie"
              :force-pro="true"
              @input="onRegieInput"
            />
          </div>
        </transition>

        <!-- Toggle Locataire -->
        <div class="af-toggle" :class="{ 'is-active': hasLocataire }" @click="toggleLocataire">
          <div class="af-toggle-check" :class="{ 'is-checked': hasLocataire }">
            <i v-if="hasLocataire" class="fas fa-check"></i>
          </div>
          <div class="af-toggle-icon af-toggle-icon--locataire">
            <i class="fas fa-user"></i>
          </div>
          <div class="af-toggle-content">
            <div class="af-toggle-title">Habitée par un locataire</div>
            <div class="af-toggle-sub">Ajouter les informations du locataire</div>
          </div>
          <div class="af-toggle-chevron" :class="{ 'is-open': hasLocataire }">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <transition name="af-slide">
          <div v-if="hasLocataire" class="af-toggle-block">
            <client-block
              title="Locataire"
              icon="fas fa-user"
              id-prefix="locataire"
              :value="dataModel.locataire"
              @input="onLocataireInput"
            />
          </div>
        </transition>

      </div>
    </transition>

  </div>
</template>

<script>
import ClientBlock from './ClientBlock.vue'

const MAPBOX_TOKEN = 'pk.eyJ1IjoidmljdG9yYW53IiwiYSI6ImNrajhuam1iOTZ1dnQydnFqbzRldDJrdW8ifQ.VkIKVhh5_Pcp7PxqQ-GCCg'
const GOUV_URL    = 'https://api-adresse.data.gouv.fr/search/'
const MAPBOX_URL  = 'https://api.mapbox.com/geocoding/v5/mapbox.places/'
const TIMEOUT_MS  = 3000

function fetchWithTimeout(url, ms = TIMEOUT_MS) {
  const ctrl  = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(timer))
}

function emptyModel() {
  return {
    adresse1    : '',
    adresse2    : '',
    code_postal : '',
    ville       : '',
    pays        : 'France',
    latitude    : null,
    longitude   : null,
    regie       : null,
    locataire   : null,
  }
}

function emptyClient(type) {
  return {
    type,
    type_client     : type === 'regie' ? 'professionnel' : 'particulier',
    nom             : '',
    prenom          : '',
    siret           : '',
    telephone       : '',
    email           : '',
    regie_id        : 0,
    proprietaire_id : 0,
  }
}

export default {
  name: 'AdresseForm',
  components: { ClientBlock },

  props: {
    value: { type: Object, default: () => ({}) },
    contextLoc: { type: Boolean, default: true }
  },

  data() {
    return {
      query         : '',
      suggestions   : [],
      highlighted   : -1,
      loading       : false,
      error         : '',
      selected      : false,
      manualMode    : false,
      isFocused     : false,
      debounceTimer : null,
      dropdownStyle : {},
      hasRegie      : false,
      hasLocataire  : false,
      dataModel     : { ...emptyModel(), ...this.value },
    }
  },

  mounted() {
    window.addEventListener('resize', this.updateDropdownPosition)
    window.addEventListener('scroll', this.updateDropdownPosition, true)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateDropdownPosition)
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
  },

  methods: {

    /* ── Dropdown position ── */
    updateDropdownPosition() {
      const el = this.$refs.inputWrapper
      if (!el || this.suggestions.length === 0) return
      const rect = el.getBoundingClientRect()
      this.dropdownStyle = {
        top   : rect.bottom + 4 + 'px',
        left  : rect.left + 'px',
        width : rect.width + 'px',
      }
    },

    /* ── Search ── */
    onInput() {
      this.selected = false
      this.error    = ''
      clearTimeout(this.debounceTimer)
      if (!this.query || this.query.length < 3) { this.suggestions = []; return }
      this.debounceTimer = setTimeout(() => this.search(), 300)
    },

    async search() {
      this.loading     = true
      this.suggestions = []
      try {
        const results = await this.searchGouv(this.query)
        if (results.length > 0) {
          this.suggestions = results
          this.highlighted = 0
          this.$nextTick(this.updateDropdownPosition)
          return
        }
        throw new Error('no_results')
      } catch {
        try {
          const fallback = await this.searchMapbox(this.query)
          this.suggestions = fallback
          this.highlighted = fallback.length > 0 ? 0 : -1
          if (fallback.length === 0) this.error = 'Aucune adresse trouvée.'
          else this.$nextTick(this.updateDropdownPosition)
        } catch {
          this.error = 'Impossible de joindre le service de géocodage.'
        }
      } finally {
        this.loading = false
      }
    },

    async searchGouv(q) {
      const url = `${GOUV_URL}?q=${encodeURIComponent(q)}&limit=5&type=housenumber`
      const res = await fetchWithTimeout(url)
      if (!res.ok) throw new Error('gouv_error')
      const data = await res.json()
      return (data.features || []).map(f => ({
        _source : 'gouv',
        _label  : f.properties.label,
        _props  : f.properties,
        _coords : f.geometry.coordinates,
      }))
    },

    async searchMapbox(q) {
      const url = `${MAPBOX_URL}${encodeURIComponent(q)}.json?autocomplete=true&limit=5&country=fr&types=address&access_token=${MAPBOX_TOKEN}`
      const res = await fetchWithTimeout(url)
      if (!res.ok) throw new Error('mapbox_error')
      const data = await res.json()
      return (data.features || []).map(f => ({
        _source : 'mapbox',
        _label  : f.place_name,
        _raw    : f,
      }))
    },

    /* ── Select ── */
    selectAddress(item) {
      if (item._source === 'gouv') {
        const p = item._props
        this.dataModel.adresse1    = p.name
        this.dataModel.code_postal = p.postcode
        this.dataModel.ville       = p.city
        this.dataModel.pays        = 'France'
        this.dataModel.longitude   = item._coords[0]
        this.dataModel.latitude    = item._coords[1]
      } else {
        const f   = item._raw
        const ctx = f.context || []
        this.dataModel.adresse1    = f.address ? `${f.address} ${f.text}` : f.place_name
        this.dataModel.code_postal = ctx.find(c => c.id.includes('postcode'))?.text || ''
        this.dataModel.ville       = ctx.find(c => c.id.includes('place'))?.text || ''
        this.dataModel.pays        = ctx.find(c => c.id.includes('country'))?.text || 'France'
        this.dataModel.longitude   = f.center[0]
        this.dataModel.latitude    = f.center[1]
      }
      this.query       = item._label
      this.suggestions = []
      this.highlighted = -1
      this.selected    = true
      this.isFocused   = false
      this.emitUpdate()
      this.$nextTick(() => this.$emit('address-selected', { ...this.dataModel }))
    },

    clear() {
      this.query        = ''
      this.selected     = false
      this.suggestions  = []
      this.error        = ''
      this.hasRegie     = false
      this.hasLocataire = false
      this.dataModel    = emptyModel()
      this.emitUpdate()
      this.$nextTick(() => this.$refs.input?.focus())
    },

    moveDown()          { this.highlighted = Math.min(this.highlighted + 1, this.suggestions.length - 1) },
    moveUp()            { this.highlighted = Math.max(this.highlighted - 1, 0) },
    selectHighlighted() { if (this.highlighted >= 0) this.selectAddress(this.suggestions[this.highlighted]) },
    closeSuggestions()  { this.suggestions = []; this.highlighted = -1 },
    onBlur()            { this.isFocused = false; setTimeout(() => this.closeSuggestions(), 150) },

    /* ── Mode manuel ── */
    switchToManual() {
      if (!this.selected) this.dataModel.adresse1 = this.query
      this.dataModel.latitude  = null
      this.dataModel.longitude = null
      this.manualMode = true
      this.emitUpdate()
    },

    switchToSearch() {
      this.manualMode   = false
      this.selected     = false
      this.query        = ''
      this.hasRegie     = false
      this.hasLocataire = false
      this.dataModel    = emptyModel()
      this.emitUpdate()
      this.$nextTick(() => this.$refs.input?.focus())
    },

    /* ── Toggles régie / locataire ── */
    toggleRegie() {
      this.hasRegie = !this.hasRegie
      // Ne réinitialiser que si on active sans données existantes
      if (this.hasRegie && !this.dataModel.regie) {
        this.dataModel.regie = emptyClient('regie')
      } else if (!this.hasRegie) {
        this.dataModel.regie = null
      }
      this.emitUpdate()
    },

    toggleLocataire() {
      this.hasLocataire = !this.hasLocataire
      if (this.hasLocataire && !this.dataModel.locataire) {
        this.dataModel.locataire = emptyClient('locataire')
      } else if (!this.hasLocataire) {
        this.dataModel.locataire = null
      }
      this.emitUpdate()
    },

    toggleLocataire() {
      this.hasLocataire        = !this.hasLocataire
      this.dataModel.locataire = this.hasLocataire ? emptyClient('locataire') : null
      this.emitUpdate()
    },

    onRegieInput(val) {
      this.dataModel.regie = val
      this.emitUpdate()
    },

    onLocataireInput(val) {
      this.dataModel.locataire = val
      this.emitUpdate()
    },

    /* ── Emit ── */
    emitUpdate() {
      this.$emit('input', { ...this.dataModel, _manual: this.manualMode })
    },
  },

  watch: {
    value: {
      deep: true,
      immediate: true,  // ← ajouter ça pour l'initialisation au montage
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.dataModel)) {
          this.dataModel = { ...emptyModel(), ...v }

          if (v.adresse1 && !this.manualMode) {
            this.query    = v.adresse1
            this.selected = true
          }

          // Charger régie et locataire depuis les objets imbriqués
          this.hasRegie     = !!v.regie
          this.hasLocataire = !!v.locataire

          if (v.regie)     this.dataModel.regie     = { ...emptyClient('regie'),     ...v.regie }
          if (v.locataire) this.dataModel.locataire = { ...emptyClient('locataire'), ...v.locataire }
        }
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

.af-root {
  font-family: 'DM Sans', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* ── Label ── */
.af-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9898a6;
  margin-bottom: 6px;
}

.af-label-sm {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #aaaab6;
  margin-bottom: 4px;
  display: block;
}

.af-label-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8102e;
  flex-shrink: 0;
}

.af-label-dot--sm {
  width: 5px;
  height: 5px;
}

.af-optional {
  font-size: 10px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #c8c8d4;
  margin-left: 2px;
}

/* ── Input principal ── */
.af-field {
  display: flex;
  flex-direction: column;
}

.af-input-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  background: #fafafa;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  overflow: hidden;
}

.af-input-wrap.is-focused,
.af-input-wrap:focus-within {
  border-color: #c8102e;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.07);
}

.af-input-wrap.is-filled {
  border-color: #e8e8ed;
  background: #fff;
}

.af-input-icon {
  flex-shrink: 0;
  width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8102e;
  font-size: 12px;
  opacity: 0.7;
}

.af-input {
  flex: 1;
  padding: 12px 4px 12px 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  background: transparent;
  border: none;
  outline: none;
  min-width: 0;
}

.af-input--plain {
  padding: 11px 14px;
  width: 100%;
  box-sizing: border-box;
}

.af-input::placeholder { color: #c4c4d0; }

.af-input-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 10px;
  flex-shrink: 0;
}

.af-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f0f0f4;
  border-top-color: #c8102e;
  border-radius: 50%;
  animation: af-spin 0.65s linear infinite;
}
@keyframes af-spin { to { transform: rotate(360deg); } }

.af-clear-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #f0f0f4;
  color: #9898a6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: background 0.15s, color 0.15s;
}
.af-clear-btn:hover { background: #c8102e; color: #fff; }

/* ── Dropdown ── */
.af-dropdown {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05);
  list-style: none;
  margin: 0;
  padding: 5px;
  overflow: hidden;
}

.af-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
}

.af-option.is-active,
.af-option:hover { background: #fff6f7; }

.af-option-pin {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fff1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8102e;
  font-size: 11px;
  flex-shrink: 0;
}

.af-option-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.af-option-main {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.af-option-sub {
  font-size: 11px;
  color: #9898a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Récap ── */
.af-recap {
  background: #fafafa;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.af-recap-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.af-recap-full { grid-column: span 2; }

.af-recap-divider {
  grid-column: span 2;
  height: 1px;
  background: #f0f0f4;
}

.af-recap-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c4c4d0;
}

.af-recap-value {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

/* ── Lien manuel ── */
.af-manual-link-wrap { text-align: right; }

.af-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #aaaab6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: color 0.15s;
}
.af-link:hover { color: #c8102e; }

/* ── Mode manuel ── */
.af-manual-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.af-manual-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.af-field--full { grid-column: span 2; }

.af-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbea;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  font-size: 12px;
  color: #92400e;
}

.af-error {
  margin: 5px 0 0;
  font-size: 12px;
  color: #c8102e;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── Toggles régie / locataire ── */
.af-extra {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.af-extra-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9898a6;
  margin-bottom: 2px;
}

.af-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  user-select: none;
}

.af-toggle:hover { background: #fff; }
.af-toggle.is-active { background: #fff; border-color: #e8e8ed; }

.af-toggle-check {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid #d4d4de;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  font-size: 10px;
}

.af-toggle-check.is-checked {
  background: #c8102e;
  border-color: #c8102e;
  color: #fff;
}

.af-toggle-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}

.af-toggle-icon--regie {
  background: #eff6ff;
  color: #2563eb;
}

.af-toggle-icon--locataire {
  background: #f0fdf4;
  color: #16a34a;
}

.af-toggle-content { flex: 1; min-width: 0; }

.af-toggle-title {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

.af-toggle-sub {
  font-size: 11px;
  color: #aaaab6;
  margin-top: 1px;
}

.af-toggle-chevron {
  color: #c4c4d0;
  font-size: 11px;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.af-toggle-chevron.is-open { transform: rotate(180deg); }

.af-toggle-block {
  padding-left: 6px;
}

/* ── Transitions ── */
.af-drop-enter-active, .af-drop-leave-active  { transition: opacity 0.15s, transform 0.15s; transform-origin: top; }
.af-drop-enter, .af-drop-leave-to             { opacity: 0; transform: scaleY(0.95) translateY(-4px); }

.af-reveal-enter-active { transition: opacity 0.2s, transform 0.2s; }
.af-reveal-enter        { opacity: 0; transform: translateY(-5px); }

.af-slide-enter-active, .af-slide-leave-active { transition: opacity 0.2s, transform 0.2s; }
.af-slide-enter, .af-slide-leave-to            { opacity: 0; transform: translateY(-6px); }
</style>